<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import CommonSettingsEditor from "@/components/widgets/CommonSettingsEditor.vue";
import Swal from "sweetalert2";
import { settingsHelperFunctions } from "@/helpers/settingsHelper";
import { useAuthStore, useCommonAppModule } from "@/state/pinia";

export default {
  name: "UserSettingsView",
  components: { Layout, PageHeader, CommonSettingsEditor },
  data() {
    return {
      settings: [],
      loading: false,
      userId: "",
    };
  },
  computed: {
    auth() {
      return useAuthStore();
    },
    commonModule() {
      return useCommonAppModule();
    },
    currentUser() {
      return this.auth.currentUser;
    },
  },
  created() {
    this.loadSettings();
  },
  methods: {
    resolveUserId() {
      if (!this.currentUser) {
        return "guest";
      }
      let userData = this.currentUser;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (error) {
          return userData || "guest";
        }
      }
      return (
        userData?.id ||
        userData?.userId ||
        userData?.uid ||
        userData?.email ||
        "guest"
      );
    },
    loadSettings() {
      this.loading = true;
      this.userId = this.resolveUserId();
      this.commonModule
        .httpGetUserSettings({ userId: this.userId })
        .then((response) => {
          this.settings = response?.settings || [];
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się załadować ustawień.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    prepareSettingsToSave(payload) {
      const settingsToSave = [];
      for (const setting of payload.settings || []) {
        const settingInput = payload.settingsInputValuesMap.get(setting.name);
        const updatedSetting = { ...setting };
        if (setting.settingType === "BOOLEAN") {
          updatedSetting.value = settingInput?.checked === true;
          settingsToSave.push(updatedSetting);
          continue;
        }
        if (setting.settingType === "DOUBLE") {
          if (settingsHelperFunctions.valueIsNotDouble(settingInput?.value)) {
            Swal.fire("Nieprawidłowa wartość", "Wprowadź poprawną liczbę dziesiętną.", "warning");
            return [];
          }
          updatedSetting.value = settingsHelperFunctions.getRoundedValue(settingInput?.value);
          settingsToSave.push(updatedSetting);
          continue;
        }
        if (setting.settingType === "INTEGER") {
          if (settingsHelperFunctions.valueIsNotInt(settingInput?.value)) {
            Swal.fire("Nieprawidłowa wartość", "Wprowadź poprawną liczbę całkowitą.", "warning");
            return [];
          }
          updatedSetting.value = parseInt(settingInput?.value, 10).toString();
          settingsToSave.push(updatedSetting);
          continue;
        }
        updatedSetting.value = settingInput?.value ?? "";
        settingsToSave.push(updatedSetting);
      }
      return settingsToSave;
    },
    saveUserSettings(payload) {
      if (this.loading) {
        return;
      }
      const settingsToSave = this.prepareSettingsToSave(payload);
      if (!settingsToSave.length) {
        return;
      }
      this.loading = true;
      this.commonModule
        .httpSaveUserSettings({ userId: this.userId, settings: settingsToSave })
        .then(() => {
          this.settings = settingsToSave;
          Swal.fire("Zapisano", "Twoje ustawienia zostały zaktualizowane.", "success");
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się zapisać ustawień.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Ustawienia" pageTitle="Użytkownik" />

    <div class="container-fluid">
      <div v-if="loading" class="d-flex justify-content-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Ładowanie...</span>
        </div>
      </div>
      <div v-else class="card">
        <div class="card-body">
          <h5 class="card-title mb-3">Preferencje użytkownika</h5>
          <CommonSettingsEditor
            :settings="settings"
            save-button-text="Zapisz ustawienia"
            @save="saveUserSettings"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>
